// src/context/SchoolContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchSchoolData } from "../services/schoolApiService";
import { applyThemeColors } from "../utils/theme";

export interface SchoolData {
  school_name: string;
  active_theme: string;
  abbreviation: string;
  logo_uri: string | false;
  logo_emblem_uri: string | false;
  theme_colors: {
    primary_color: string;
    secondary_color: string;
    primary_color_light: string;
    primary_color_dark: string;
    secondary_color_light: string;
  };
  calendar_link?: string;
  clubs_workshops?: {
    clubs_workshops_link?: string;
    clubs_workshops_description?: string;
  };
  onboarding_content?: {
    school_info_ready?: boolean;
    first_week_description?: string;
    general_school_expectations?: string;
    homework?: string;
    quizzes_and_tests?: string;
    quizzes_and_tests_learning_coach?: string;
    login_information?: string;
    school_start_faqs?: {
      faq_title: string;
      faq_content: string;
      audience: string;
      school_type: string;
    }[];
    /** Learning Center secondary pages keyed by `pageId` (e.g. `"400"`), same shape as `public/data/pages/{pageId}.json` */
    checklist_secondary_pages?: Record<string, unknown>;
    [key: string]: any;
  };
}

interface SchoolContextType {
  data: SchoolData | null;
  loading: boolean;
  error: string | null;
}

const SchoolContext = createContext<SchoolContextType | undefined>(undefined);

interface SchoolProviderProps {
  schoolId: string | null;
  studentId: string | null;
  children: React.ReactNode;
}

export const SchoolProvider: React.FC<SchoolProviderProps> = ({ schoolId, studentId, children }) => {
  const [data, setData] = useState<SchoolData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSchoolData = async () => {
      if (!schoolId || !studentId) {
        setLoading(false);
        setData(null);
        setError(null);
        return;
      }

      console.log("Fetching school data for schoolId:", schoolId);
      setLoading(true);

      try {
        const result = await fetchSchoolData(schoolId);
        if (result) {
          setData(result);
          setError(null);

          // Apply theme colors if available
          if (result.theme_colors) {
            applyThemeColors({
              ...result.theme_colors,
              ...(result.logo_uri && typeof result.logo_uri === "string"
                ? { logo_uri: `url(${result.logo_uri})` }
                : {}),
              ...(result.logo_emblem_uri && typeof result.logo_emblem_uri === "string"
                ? { logo_emblem_uri: `url(${result.logo_emblem_uri})` }
                : {}),
            });
          }
        } else {
          setError("Failed to load school data");
          setData(null);
        }
      } catch (err) {
        setError("Unexpected error while loading school data");
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    loadSchoolData();
  }, [schoolId, studentId]);

  return (
    <SchoolContext.Provider value={{ data, loading, error }}>
      {children}
    </SchoolContext.Provider>
  );
};

export const useSchool = (): SchoolContextType => {
  const context = useContext(SchoolContext);
  if (!context) {
    throw new Error("useSchool must be used within a SchoolProvider");
  }
  return context;
};
